import { defineStore, storeToRefs } from 'pinia'
import { computed, readonly, ref } from 'vue'
import { postStylistChatbotMessage, getStylingSuggestedQueries, getStylingChatbotMessageHistory, postStylingChatbotFeedback } from '@shared/api/styling.js'
import { useClientStore } from '@shared/stores/client.js'
import { useClosetStore } from '@shared/stores/closet.js'

export const useStylingChatbotStore = defineStore('stylingChatbot', () => {
  const closetStore = useClosetStore()

  const { firstName } = storeToRefs(useClientStore())
  
  const displayName = computed(() => {
    if (!firstName.value || firstName.value.length > 10) return 'there'
    return firstName.value
  })
  const greetingMessage = computed(() => { return `Hello ${displayName.value}`})
  const greetingQuestion = ref('What can I help you find today?')
  const suggestedQueries = ref([])
  const inIntro = ref(true)
  const fetchingMessageHistory = ref(false)
  const messageHistory = ref([])
  const messageHistoryNextPage = ref(null)
  const disableTextArea = ref(false)
  const messageFeedbacks = ref({}) // to store feedback for each message
  const ratingDetailOptions = readonly(ref([
    { value: 1, label: 'Irrelevant' },
    { value: 2, label: 'Personal Taste' },
    { value: 3, label: 'Other' }
  ]))
  const fetchHistoryErrorMessage = ref('')
  const allowScroll = ref(true)


  function resetErrorMessage() {
    fetchHistoryErrorMessage.value = ''
  }
  async function fetchMessageHistory() {
    fetchingMessageHistory.value = true
    resetErrorMessage()
    try {
      const response = await getStylingChatbotMessageHistory(messageHistoryNextPage.value)
      const newMessages = response.data.results.toReversed()
      if (!messageHistoryNextPage.value) {
        messageHistory.value = []
      }
      // serverId differentiates from the tempId used in the UI while waiting for the server response
      for (const message of newMessages) {
        message.serverId = message.id
      }
      messageHistory.value = [...newMessages, ...messageHistory.value]
      messageHistoryNextPage.value = response.data.next
      for (const message of newMessages) {
        closetStore.updateStyleColorsMap(message.recommendations)
        messageFeedbacks.value[message.id] = {
          id: message.id,
          rating: message.clientRating?.rating || 0,
          detail: message.clientRating?.detail || null,
          suggestion: message.clientRating?.suggestion || null
        }
      }
    } catch (error) {
      fetchHistoryErrorMessage.value = 'An error occurred while fetching message history. Please try again.'
    } finally {
      fetchingMessageHistory.value = false
    }
  }
  async function fetchSuggestedQueries () {
    const response = await getStylingSuggestedQueries()
    suggestedQueries.value = [...response.data]
  }

  async function postNewMessage(message) {
    disableTextArea.value = true
    resetErrorMessage()
    const trimmed = message.trim()
    if (!trimmed) return

    const tempId = `temp-${Date.now()}`
  
    const tempMessage = {
      id: tempId, // stable key
      clientMessage: message
    }
    messageHistory.value = [...messageHistory.value, tempMessage]
    try {
      const response = await postStylistChatbotMessage({ message })
      const messageToUpdate = messageHistory.value[messageHistory.value.length - 1]
      if (messageToUpdate) {
        messageToUpdate.serverId = String(response.data.id)
        messageToUpdate.clientMessage = response.data.clientMessage
        messageToUpdate.llmMessage = response.data.llmMessage
        messageToUpdate.recommendations = response.data?.recommendations
        messageToUpdate.llmFollowUpPrompt	 = response.data?.llmFollowUpPrompt
        messageToUpdate.clientRating = response.data?.clientRating || { rating: 0, detail: '', suggestion: '' }
      }
      
      if (response.data.recommendations) {
        closetStore.updateStyleColorsMap(response.data.recommendations)
      }
      disableTextArea.value = false

      // to-do: clean this up in conversational search fast follows [ENG-5080]
      // id is not stable, so we need to fetch the message history again to get the serverId
      const historyResponse = await getStylingChatbotMessageHistory()
      const serverId = historyResponse.data.results[0]?.id || null
      
      if (messageToUpdate && serverId) {
        messageToUpdate.serverId = String(serverId)
      }
      
      // add the new message to the feedbacks object to initialize it
      messageFeedbacks.value[serverId] = {
        id: serverId
      }
    } catch (err) {
      const messageToUpdate = messageHistory.value[messageHistory.value.length - 1]
      const status = err?.response?.status  
      if (status === 400) {
        messageToUpdate.llmMessage = 'Hmm, I had an issue processing that. Mind giving it another try?'
      } else {
        messageToUpdate.llmMessage = 'Oops, something went wrong on my end. Mind giving it another try?'
      }
    } finally{
      disableTextArea.value = false
    }

  }
  
  async function postFeedback (feedback) {
    allowScroll.value = false
    const message = messageHistory.value.find(message => message.serverId === feedback.id)
    try {
      await postStylingChatbotFeedback(feedback)
      message.feedbacksError = ''
    } catch (error) {
      message.feedbackError = 'An error occurred while submitting your feedback. Please try again.'
    }
  }

  return {
    greetingMessage,
    greetingQuestion,
    suggestedQueries,
    inIntro,
    allowScroll,
    fetchingMessageHistory,
    messageHistory,
    messageHistoryNextPage,
    disableTextArea,
    messageFeedbacks,
    ratingDetailOptions,
    fetchHistoryErrorMessage,
    fetchMessageHistory,
    fetchSuggestedQueries,
    postNewMessage,
    postFeedback
  }
})
